import React, { useEffect, useState } from "react";
import SquarePayment from "./SquarePayment";
import StripePayment from "./StripePayment";
import RazorpayPayment from "./RazorpayPayment";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { getPaymentOptions, getUpiQr, PaymentOptions } from "../services/api";
import { activeCurrencySymbol } from "../utils/currency";

// Base interface for common properties
interface BasePaymentProps {
  pricing: number | null;
  handleOrder: (paymentMethod: string, transactionId?: string) => void;
  isOpen: boolean;
  onClose: () => void;
  productDescription: string;
  phone: string;
  /** Restaurant taking the payment — its own gateway keys are used. */
  restaurantId: string;
  /**
   * Customer says they paid by scanning the restaurant's QR. Can't be
   * verified automatically, so the order goes to staff for approval.
   */
  onQrPaid?: () => void;
}

type Gateway = "razorpay" | "square" | "stripe" | "qr";

const GATEWAY_LABELS: Record<Gateway, string> = {
  razorpay: "Razorpay",
  square: "Card (Square)",
  stripe: "Card (Stripe)",
  qr: "Scan QR",
};

export default function PaymentPage(props: BasePaymentProps) {
  const { pricing, handleOrder, isOpen, onClose, restaurantId, phone, onQrPaid } = props;
  const [qrSubmitting, setQrSubmitting] = useState(false);
  const [options, setOptions] = useState<PaymentOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState<Gateway | null>(null);
  // QR generated from the restaurant's UPI ID with this order's amount.
  const [upiQr, setUpiQr] = useState<string>("");
  useEffect(() => {
    if (active !== "qr" || !options?.gateways.qr?.upiId || !pricing) return;
    setUpiQr("");
    getUpiQr(restaurantId, pricing)
      .then((res) => setUpiQr(res.data.data.dataUrl))
      .catch(() => setUpiQr(""));
  }, [active, options, pricing, restaurantId]);

  // Which gateways this restaurant has set up (Razorpay in India, else Square/Stripe).
  useEffect(() => {
    if (!isOpen || !restaurantId) return;
    setLoading(true);
    getPaymentOptions(restaurantId)
      .then((res) => {
        const data = res.data.data;
        setOptions(data);
        // Skip gateways this build doesn't know (e.g. a newer server), so a
        // blank tab never shows.
        const available = (Object.keys(data.gateways) as Gateway[]).filter((g) => g in GATEWAY_LABELS);
        setActive(available[0] || null);
      })
      .catch(() => setOptions(null))
      .finally(() => setLoading(false));
  }, [isOpen, restaurantId]);

  const onPaid = (paymentMethod: string, transactionId?: string) => {
    handleOrder(paymentMethod, transactionId);
    onClose();
  };

  const gateways = (options ? Object.keys(options.gateways) : []).filter(
    (g) => g in GATEWAY_LABELS,
  ) as Gateway[];

  const renderGateway = () => {
    if (!options || !active || !pricing) return null;
    if (active === "razorpay" && options.gateways.razorpay) {
      return (
        <RazorpayPayment
          pricing={pricing}
          restaurantId={restaurantId}
          phone={phone}
          handleOrder={onPaid}
        />
      );
    }
    if (active === "square" && options.gateways.square) {
      const { applicationId, locationId, countryCode } = options.gateways.square;
      return (
        <SquarePayment
          pricing={pricing}
          restaurantId={restaurantId}
          applicationId={applicationId}
          locationId={locationId}
          countryCode={countryCode}
          handleOrder={onPaid}
        />
      );
    }
    if (active === "qr" && options.gateways.qr) {
      const { imageUrl, upiId, payeeName } = options.gateways.qr;
      const qrSrc = upiId ? upiQr : imageUrl;
      return (
        <div className="flex flex-col items-center gap-3 p-4 text-center">
          <p className="m-0 text-lg font-semibold">
            Scan & pay {activeCurrencySymbol()}
            {pricing.toFixed(2)}
          </p>
          {qrSrc ? (
            <img
              src={qrSrc}
              alt="Payment QR code"
              className="h-64 w-64 rounded-md border object-contain"
            />
          ) : (
            <div className="flex h-64 w-64 items-center justify-center rounded-md border text-sm text-gray-400">
              Generating QR...
            </div>
          )}
          {payeeName && <p className="m-0 text-sm text-gray-700">{payeeName}</p>}
          {upiId && <p className="m-0 font-mono text-sm text-gray-600">{upiId}</p>}
          <p className="m-0 text-xs text-gray-500">
            After paying, tap below. Staff will confirm your payment before preparing the order.
          </p>
          <button
            type="button"
            disabled={qrSubmitting || !onQrPaid}
            onClick={async () => {
              setQrSubmitting(true);
              try {
                await onQrPaid?.();
                onClose();
              } finally {
                setQrSubmitting(false);
              }
            }}
            className="w-full rounded-md bg-black px-4 py-3 font-semibold text-white disabled:opacity-60"
          >
            {qrSubmitting ? "Sending..." : "I've paid"}
          </button>
        </div>
      );
    }
    if (active === "stripe" && options.gateways.stripe) {
      return (
        <StripePayment
          pricing={pricing}
          restaurantId={restaurantId}
          publishableKey={options.gateways.stripe.publishableKey}
          handleOrder={onPaid}
        />
      );
    }
    return null;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-[600px] bg-white z-[99999]">
        <DialogHeader>
          <DialogTitle>{"Purchase Content"}</DialogTitle>
        </DialogHeader>

        {!pricing || pricing <= 0 ? (
          <p className="text-center text-gray-500">Invalid payment amount</p>
        ) : loading ? (
          <p className="text-center text-gray-500">Loading payment options...</p>
        ) : gateways.length === 0 ? (
          <p className="text-center text-gray-500">
            Online payment isn't set up for this restaurant yet. Please pay at the counter.
          </p>
        ) : (
          <>
            {gateways.length > 1 && (
              <div className="mb-2 flex gap-2">
                {gateways.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setActive(g)}
                    className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold transition ${
                      active === g
                        ? "border-black bg-black text-white"
                        : "border-gray-300 bg-white text-gray-700 hover:border-gray-500"
                    }`}
                  >
                    {GATEWAY_LABELS[g]}
                  </button>
                ))}
              </div>
            )}
            {renderGateway()}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
